import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CACES_CATEGORIES } from "@/constants";

/**
 * Bloc CACES + dates d'expiration pour la fiche admin d'un candidat.
 *
 * Props:
 *  - caces:        { [key: string]: bool }
 *  - cacesExpiry:  { [key: string]: "YYYY-MM-DD" }
 *  - onCacesChange(next)         → toute la map CACES (checked)
 *  - onCacesExpiryChange(next)   → toute la map des dates d'expiration
 */
export default function CandidateCacesCard({ caces = {}, cacesExpiry = {}, onCacesChange, onCacesExpiryChange }) {
  const today = new Date();
  const daysUntil = (iso) => Math.ceil((new Date(iso) - today) / 86400000);

  const toggle = (key, checked) => onCacesChange({ ...caces, [key]: !!checked });
  const setExpiry = (key, val) => onCacesExpiryChange({ ...cacesExpiry, [key]: val });

  return (
    <div className="border-t border-gray-200 pt-4" data-testid="caces-card">
      <h4 className="text-sm font-bold text-[#662483] mb-3">CACES - Certificats</h4>
      <p className="text-[10px] text-gray-500 mb-3 italic">
        Renseignez la date d&apos;expiration pour recevoir une alerte automatique 2 mois avant.
      </p>
      <div className="space-y-3">
        {CACES_CATEGORIES.map(({ key, label }) => {
          const checked = !!caces[key];
          const expiry = cacesExpiry[key] || "";
          const days = expiry ? daysUntil(expiry) : null;
          const expiring = days !== null && days >= 0 && days <= 60;
          const expired = days !== null && days < 0;
          const cls = expired ? "bg-red-50 border border-red-200" : expiring ? "bg-amber-50 border border-amber-200" : "";
          return (
            <div key={key} className={`flex items-start gap-3 p-2 ${cls}`} data-testid={`caces-${key}`}>
              <Checkbox
                checked={checked}
                onCheckedChange={(c) => toggle(key, c)}
                className="mt-0.5"
                data-testid={`caces-checkbox-${key}`}
              />
              <div className="flex-1 min-w-0">
                <label
                  className="text-xs text-gray-700 leading-relaxed cursor-pointer block"
                  onClick={() => toggle(key, !checked)}
                >
                  {label}
                </label>
                {checked && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <Label className="text-[10px] text-gray-500 uppercase tracking-wider">Expire le</Label>
                    <Input
                      type="date"
                      value={expiry}
                      onChange={(e) => setExpiry(key, e.target.value)}
                      className="rounded-none text-xs h-7 w-40"
                      data-testid={`caces-expiry-${key}`}
                    />
                    {expired && (
                      <Badge className="rounded-none bg-red-100 text-red-700 text-[10px]">
                        ⚠️ Expiré depuis {-days}j
                      </Badge>
                    )}
                    {expiring && !expired && (
                      <Badge className="rounded-none bg-amber-100 text-amber-700 text-[10px]">
                        ⏰ Expire dans {days}j
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
